import Option from '../../entities/option';
import {DEFAULT_CALENDAR_EVENT_KINDS, InterestMethods, LeadPeriodChoices, RoundingMethods} from './OptionConstants';

export type OptionDefault = {defaultValue: any, type: string};

/**
 * The one table of option defaults, read by useOptions and OptionHelper (OWA-1586).
 *
 * A label in the schema's default entries (Option.getDefaultEntries, generated from the option
 * schema) takes its default from there, because that entry is what a firm that saved nothing gets
 * at runtime. Those labels are not repeated below. Every other label is listed here once, keyed
 * by the label text (the values of OptionNames and OptionHelper's constants).
 */
const CODED_DEFAULTS: Record<string, OptionDefault> = {
    billingIncrement: {defaultValue: 1, type: 'numeric'},
    paymentEmailClient: {defaultValue: true, type: 'boolean'},
    paymentEmailPayer: {defaultValue: true, type: 'boolean'},
    paymentEmailSelf: {defaultValue: true, type: 'boolean'},
    paymentPortalBenefactorsLink: {defaultValue: true, type: 'boolean'},
    invoiceFontFamily: {defaultValue: 'Open Sans, sans-serif', type: 'string'},
    invoiceFontSize: {defaultValue: '10pt', type: 'string'},
    invoiceFirmAddress: {defaultValue: null, type: 'string'},
    invoiceFirmLetterhead: {defaultValue: 'Set up letterhead in Options/Invoicing', type: 'string'},
    // No default: until a firm sets a width, the image keeps its own size.
    invoiceLetterheadWidth: {defaultValue: null, type: 'numeric'},
    paidConsultPrice: {defaultValue: 250, type: 'numeric'},
    paidConsultDuration: {defaultValue: 1, type: 'numeric'},
    paidConsultName: {defaultValue: 'Initial Consultation', type: 'string'},
    paidConsultActivityId: {defaultValue: 'aea93c81-c896-4116-82f0-99864deef232', type: 'string'},
    leadFollowupActivityId: {defaultValue: '7a87b303-ce41-428e-98b2-bfdca74d2a83', type: 'string'},
    leadInitialConsultUser: {defaultValue: '', type: 'string'},
    useCaseTrust: {defaultValue: true, type: 'boolean'},
    invoiceDefaultSummary: {defaultValue: '', type: 'string'},
    invoiceNarrativeHeader: {defaultValue: 'Work We Did for You', type: 'string'},
    invoiceTerms: {defaultValue: 0, type: 'numeric'},
    taskEmojis: {defaultValue: false, type: 'boolean'},
    roundingMethod: {defaultValue: RoundingMethods.ROUNDING_METHOD_MIDDLE, type: 'string'},
    showHelp: {defaultValue: true, type: 'boolean'},
    soundForTaskComplete: {defaultValue: true, type: 'boolean'},
    soundForWin: {defaultValue: true, type: 'boolean'},
    paymentAllocationMethod: {defaultValue: 'proportional', type: 'string'},
    utilizationHolidaysPerYear: {defaultValue: 11, type: 'numeric'},
    utilizationVacationDays: {defaultValue: 15, type: 'numeric'},
    calendarEnforceWalls: {defaultValue: true, type: 'boolean'},
    calendarEventKinds: {defaultValue: DEFAULT_CALENDAR_EVENT_KINDS, type: 'string'},
    sessionReplayOptOut: {defaultValue: false, type: 'boolean'},
    paymentPortal: {defaultValue: true, type: 'boolean'},
    paymentPortalBenefactorsInvoice: {defaultValue: false, type: 'boolean'},
    invoiceTaxRate: {defaultValue: 0, type: 'numeric'},
    invoiceTaxLabel: {defaultValue: '', type: 'string'},
    invoiceInterestRate: {defaultValue: 0, type: 'numeric'},
    invoiceInterestMethod: {defaultValue: InterestMethods.SIMPLE_MONTHLY, type: 'string'},
    invoiceInterestGracePeriod: {defaultValue: 0, type: 'numeric'},
    invoiceLeadPeriod: {defaultValue: LeadPeriodChoices.UP_TO_CONSULT, type: 'string'},
};

/** The labels the table lists itself (a test checks them against OptionNames). */
export const CODED_DEFAULT_LABELS: readonly string[] = Object.keys(CODED_DEFAULTS);

const FALLBACK: OptionDefault = {defaultValue: '', type: 'string'};

/** The default a schema default entry stands for: booleans are stored as 'true' or 'false' text. */
function schemaDefault(entry: Option): OptionDefault {
    return {
        defaultValue: entry.type === 'boolean' ? entry.value === 'true' : entry.value,
        type: entry.type,
    };
}

/**
 * The default value and type for an option label. Unknown labels default to the empty string.
 */
export function getOptionDefaults(label: string): OptionDefault {
    const entry = Option.getDefaultEntries().find(o => o.label === label);
    if (entry) return schemaDefault(entry);

    return CODED_DEFAULTS[label] ?? FALLBACK;
}
